import * as yup from 'yup';
import { useExperiment } from '@web/experiments';
import { requiredImageFromCMSSchema, richTextFromCMSSchema } from '../../cms';

export const quizNextButtonSchema = yup
  .object({
    desktopCTA: yup.string(),
    mobileCTA: yup.string(),
  })
  .transform(value => {
    let { value: experimentValue } = useExperiment(value.test_name ?? null);
    experimentValue = experimentValue || 'control';
    let ctaVariants = value;
    if (ctaVariants?.component === 'flagship_ab_test') {
      const experimentVariation = ctaVariants.variants.find(
        variant => variant.variant_name === experimentValue,
      );
      ctaVariants = experimentVariation?.blocks[0] ?? [];
    }
    return {
      desktopCTA: ctaVariants.desktopCTA || 'Continue',
      mobileCTA: ctaVariants.mobileCTA || 'Continue',
    };
  });

const useQuestionExperimentValues = value => {
  let { value: experimentValue } = useExperiment(
    value.question[0].test_name ?? null,
  );
  experimentValue = experimentValue || 'control';
  let questionVariants = value.question[0];
  const experimentVariation = questionVariants?.variants.find(
    variant => variant.variant_name === experimentValue,
  );

  questionVariants = experimentVariation?.blocks[0] ?? [];
  return questionVariants;
};

export const userInfoSchema = yup
  .object({
    headline: yup.string(),
    subheadline: yup.string(),
    emailLabel: yup.string().required(),
    emailQuestionCaption: yup.string().required(),
    emailQuestionPrompt: yup.string().required(),
    firstNameLabel: yup.string().required(),
    firstNameQuestionPrompt: yup.string().required(),
    bannerMessage: yup.string(),
    ctaButtonText: quizNextButtonSchema,
    discountBannerCopy: yup.string(),
    discountBannerBgColor: yup.string(),
    discountBannerFontColor: yup.string(),
  })
  .transform(value => {
    let question = null;
    if (value.question[0].component === 'flagship_ab_test') {
      question = useQuestionExperimentValues(value);
    }
    return {
      headline: value.headline,
      subheadline: value.subheadline,
      emailLabel: question?.email_label || value.question[0].email_label,
      emailQuestionCaption:
        question?.caption ||
        value.question[0].caption ||
        "We'll use this to save your recommendations so you can come back to them.",
      emailQuestionPrompt:
        question?.emailPrompt ||
        value.question[0].emailPrompt ||
        "What's your email?",
      firstNameLabel:
        question?.first_name_label || value.question[0].first_name_label,
      firstNameQuestionPrompt:
        question?.namePrompt ||
        value.question[0].namePrompt ||
        "What's your name?",
      bannerMessage:
        question?.banner_message || value.question[0].banner_message,
      ctaButtonText: question?.button?.[0] || value.question[0].button[0],
      discountBannerCopy:
        value.question[0].discount_banner_copy ||
        'Get {{offer}} of your starter box!',
      discountBannerBgColor: value.question[0].discount_banner_bgColor,
      discountBannerFontColor: value.question[0].discount_banner_fontColor,
    };
  });

export const pupInfoSchema = yup
  .object({
    labelName: yup.string().required(),
    genderList: yup.array().required(),
    pupInfoQuestionCaption: yup.string().required(),
    pupInfoQuestionPrompt: yup.string().required(),
    ctaButtonText: quizNextButtonSchema,
    discountBannerCopy: yup.string(),
    discountBannerBgColor: yup.string(),
    discountBannerFontColor: yup.string(),
  })
  .transform(value => {
    let question = null;
    if (value.question[0].component === 'flagship_ab_test') {
      question = useQuestionExperimentValues(value);
    }
    return {
      labelName: question?.pup_name_label || value.question[0].pup_name_label,
      genderList: [
        {
          name: question?.male_chip_text || value.question[0].male_chip_text,
          value: 'Male',
        },
        {
          name:
            question?.female_chip_text || value.question[0].female_chip_text,
          value: 'Female',
        },
      ],
      pupInfoQuestionCaption:
        question?.caption ||
        value.question[0].caption ||
        'If you have more than one, you can create additional pup profiles at checkout or from your account.',
      pupInfoQuestionPrompt:
        question?.prompt ||
        value.question[0].prompt ||
        'Tell us about your pup!',
      ctaButtonText: question?.button?.[0] || value.question[0]?.button?.[0],
      discountBannerCopy:
        value.question[0].discount_banner_copy ||
        'Get {{offer}} of your starter box!',
      discountBannerBgColor: value.question[0].discount_banner_bgColor,
      discountBannerFontColor: value.question[0].discount_banner_fontColor,
    };
  });

export const birthdaySchema = yup
  .object({
    labelDay: yup.string().required(),
    labelMonth: yup.string().required(),
    labelYear: yup.string().required(),
    pupBirthdayQuestionCaption: yup.string().required(),
    pupBirthdayQuestionPrompt: yup.string().required(),
    pupBirthdayQuestionSubheadline: yup.string(),
    ctaButtonText: quizNextButtonSchema,
  })
  .transform(value => {
    let question = null;
    if (value.question[0].component === 'flagship_ab_test') {
      question = useQuestionExperimentValues(value);
    }
    return {
      labelDay: question?.day_label || value.question[0].day_label,
      labelMonth: question?.month_label || value.question[0].month_label,
      labelYear: question?.year_label || value.question[0].year_label,
      pupBirthdayQuestionCaption:
        question?.caption ||
        value.question[0].caption ||
        "If you're unsure, give us your best guess.",
      pupBirthdayQuestionPrompt:
        question?.prompt ||
        value.question[0].prompt ||
        "When is {{pupName}}'s birthday?",
      pupBirthdayQuestionSubheadline:
        question?.prompt || value.question[0].subheadline,
      ctaButtonText: question?.button[0] || value.question[0].button[0],
    };
  });

export const breedSchema = yup
  .object({
    breedQuestionPrompt: yup.string().required(),
    labelBreed: yup.string().required(),
    ctaButtonText: quizNextButtonSchema,
  })
  .transform(value => {
    let question = null;
    if (value.question[0].component === 'flagship_ab_test') {
      question = useQuestionExperimentValues(value);
    }
    return {
      breedQuestionPrompt:
        question?.prompt || value.question[0].prompt || value.headline,
      labelBreed: question?.labelBreed || "Type {{pupName}}'s breed",
      ctaButtonText: question?.button[0] || value.question[0].button[0],
    };
  });

export const caloriesSchema = yup
  .object({
    idealWeightHelp: yup.string().required(),
    idealWeightLabel: yup.string().required(),
    puppyIdealWeightHelp: yup.string().required(),
    puppyIdealWeightLabel: yup.string().required(),
    puppySpayNeuterNotice: yup.string().required(),
    spayedChipLabel: yup.string().required(),
    spayedStatusQuestionCaption: yup.string().required(),
    spayedStatusQuestionPrompt: yup.string().required(),
    unspayedChipLabel: yup.string().required(),
    weightLabel: yup.string().required(),
    weightQuestionPrompt: yup.string().required(),
    ctaButtonText: quizNextButtonSchema,
    discountBannerCopy: yup.string(),
    discountBannerBgColor: yup.string(),
    discountBannerFontColor: yup.string(),
    weightQuestionSubheadline: yup.string(),
  })
  .transform(value => {
    let question = null;
    if (value.question[0].component === 'flagship_ab_test') {
      question = useQuestionExperimentValues(value);
    }
    return {
      idealWeightHelp:
        "If added, we'll use this weight to determine your pup's daily calories.",
      idealWeightLabel: "Pup's ideal weight in lbs (optional)",
      puppyIdealWeightHelp:
        "We'll use adult weight to ensure your pup gets the right amount of food as they grow",
      puppyIdealWeightLabel: "Pup's future adult weight in lbs (optional)",
      puppySpayNeuterNotice:
        'Select {{pupStatusTermLowercase}} if you plan to {{pupStatusTermVerb}} your puppy as they reach maturity.',
      spayedChipLabel: '{{pupStatusTermUppercase}}',
      spayedStatusQuestionCaption:
        question?.caption ||
        value.question[0].caption ||
        'Spayed and neutered pups require fewer calories.',
      spayedStatusQuestionPrompt: '{{pupPronounUppercase}} is',
      unspayedChipLabel: 'Not {{pupStatusTermUppercase}}',
      weightLabel: "Pup's current weight in lbs",
      weightQuestionPrompt:
        question?.prompt ||
        value.question[0].prompt ||
        "Next, we need info to calculate your pup's daily calories.",
      ctaButtonText: question?.button[0] || value.question[0].button[0],
      discountBannerCopy:
        value.question[0].discount_banner_copy ||
        'Get {{offer}} of your starter box!',
      discountBannerBgColor: value.question[0].discount_banner_bgColor,
      discountBannerFontColor: value.question[0].discount_banner_fontColor,
    };
  });

export const activitySchema = yup
  .object({
    activityQuestionPrompt: yup.string().required(),
    levels: yup
      .array(
        yup.object({
          name: yup.string().required(),
          value: yup.string().required(),
          description: yup.string().required(),
          image: requiredImageFromCMSSchema,
        }),
      )
      .required(),
    subheadline: yup.string(),
    ctaButtonText: quizNextButtonSchema,
    discountBannerCopy: yup.string(),
    discountBannerBgColor: yup.string(),
    discountBannerFontColor: yup.string(),
  })
  .transform(value => {
    let question = null;
    if (value.question[0].component === 'flagship_ab_test') {
      question = useQuestionExperimentValues(value);
    }
    return {
      activityQuestionPrompt:
        question?.prompt ||
        value.question[0].prompt ||
        'How active is {{pupName}}?',
      levels: [
        {
          name:
            question?.low_activity_card_title ||
            value.question[0].low_activity_card_title,
          value: 'Low',
          image:
            question?.low_activity_image ||
            value.question[0].low_activity_image,
          description:
            question?.low_activity_card_description ||
            value.question[0].low_activity_card_description,
        },
        {
          name:
            question?.medium_activity_card_title ||
            value.question[0].medium_activity_card_title,
          value: 'Moderate',
          image:
            question?.medium_activity_image ||
            value.question[0].medium_activity_image,
          description:
            question?.medium_activity_card_description ||
            value.question[0].medium_activity_card_description,
        },
        {
          name:
            question?.high_activity_card_title ||
            value.question[0].high_activity_card_title,
          value: 'High',
          image:
            question?.high_activity_image ||
            value.question[0].high_activity_image,
          description:
            question?.high_activity_card_description ||
            value.question[0].high_activity_card_description,
        },
      ],
      subheadline:
        question?.caption || value.question[0].caption || value.subheadline,
      ctaButtonText: question?.button[0] || value.question[0].button[0],
      discountBannerCopy:
        value.question[0].discount_banner_copy ||
        'Get {{offer}} of your starter box!',
      discountBannerBgColor: value.question[0].discount_banner_bgColor,
      discountBannerFontColor: value.question[0].discount_banner_fontColor,
    };
  });

export const poopSchema = yup
  .object({
    poopQuestionPrompt: yup.string().required(),
    subheadline: yup.string(),
    ctaText: yup.string().required(),
    responses: yup.array().required(),
    modal: yup.object().required(),
    ctaButtonText: quizNextButtonSchema,
  })
  .transform(value => {
    let question = null;
    if (value.question[0].component === 'flagship_ab_test') {
      question = useQuestionExperimentValues(value);
    }
    return {
      poopQuestionPrompt:
        question?.prompt || value.question[0].prompt || value.headline,
      subheadline:
        question?.caption || value.question[0].caption || value.subheadline,
      ctaText: value.cta_text,
      responses: [
        {
          name: question?.runny_title || value.question[0].runny_title,
          value: question?.runny_response || value.question[0].runny_response,
        },
        {
          name: question?.mushy_title || value.question[0].mushy_title,
          value: question?.mushy_response || value.question[0].mushy_response,
        },
        {
          name:
            question?.about_right_title || value.question[0].about_right_title,
          value:
            question?.about_right_response ||
            value.question[0].about_right_response,
        },
        {
          name: question?.hard_title || value.question[0].hard_title,
          value: question?.hard_response || value.question[0].hard_response,
        },
        {
          name:
            question?.unpredictable_title ||
            value.question[0].unpredictable_title,
          value:
            question?.unpredictable_response ||
            value.question[0].unpredictable_response,
        },
      ],
      modal: question?.modal || value.question[0].modal,
      ctaButtonText: question?.button[0] || value.question[0].button[0],
    };
  });

export const allergiesSchema = yup
  .object({
    allergySearchLabel: yup.string().required(),
    allergiesQuestionPrompt: yup.string().required(),
    allergiesQuestionCaption: yup.string(),
    placeholderAllergiesCount: yup.number().required(),
    ctaButtonText: quizNextButtonSchema,
  })
  .transform(value => {
    let question = null;
    if (value.question[0].component === 'flagship_ab_test') {
      question = useQuestionExperimentValues(value);
    }
    return {
      allergySearchLabel: 'Other',
      allergiesQuestionPrompt:
        question?.prompt ||
        value.question[0].prompt ||
        'Does {{pupPronounLowercase}} have any food allergies or sensitivities?',
      allergiesQuestionCaption: question?.caption || value?.question[0]?.prompt,
      placeholderAllergiesCount: 9,
      petPronoun: '{{pupPronounLowercase}}',
      ctaButtonText: value.question[0].button[0],
    };
  });

export const aboutSchema = yup.object({});

export const nutritionSchema = yup.object({});

export const onboardingInterstitialSchema = yup
  .object({
    eyebrow_text: yup.string(),
    headline: yup.string().required(),
    image: requiredImageFromCMSSchema,
    subheadline: richTextFromCMSSchema,
  })
  .transform(value => {
    let question = null;
    if (value.question[0].component === 'flagship_ab_test') {
      question = useQuestionExperimentValues(value);
    }
    return {
      eyebrowText: question?.eyebrow_text || value.question?.[0].eyebrow_text,
      headline: question?.headline || value.question?.[0].headline,
      image: question?.image || value.question?.[0].image,
      subheadline: question?.subheadline || value.question?.[0].subheadline,
    };
  });
